// src/features/streams/components/simulcast/RtmpDestinationForm.tsx

import { Stack, TextInput, PasswordInput, Switch, Group, Button, SimpleGrid } from '@mantine/core';
import { useForm, useController } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { rtmpFormSchema, type RtmpFormData } from '../../schemas/SimulcastForm.schema';
import { SIMULCAST_CONSTRAINTS } from '@/lib/constants/simulcast';

interface RtmpDestinationFormProps {
  defaultValues?: RtmpFormData;
  onSubmit: (data: RtmpFormData) => void;
  onCancel: () => void;
  isPending: boolean;
  submitLabel?: string;
}

const RTMP_DEFAULTS: RtmpFormData = {
  label: '',
  rtmpServerUrl: '',
  rtmpStreamKey: '',
  rtmpUser: '',
  rtmpPass: '',
  retry: true,
};

export function RtmpDestinationForm({
  defaultValues,
  onSubmit,
  onCancel,
  isPending,
  submitLabel = 'Create',
}: RtmpDestinationFormProps) {
  const { control, handleSubmit, formState: { isDirty } } = useForm<RtmpFormData>({
    resolver: zodResolver(rtmpFormSchema),
    defaultValues: defaultValues ?? RTMP_DEFAULTS,
  });

  const labelField = useController({ name: 'label', control });
  const serverUrlField = useController({ name: 'rtmpServerUrl', control });
  const streamKeyField = useController({ name: 'rtmpStreamKey', control });
  const userField = useController({ name: 'rtmpUser', control });
  const passField = useController({ name: 'rtmpPass', control });
  const retryField = useController({ name: 'retry', control });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap="md">
        <TextInput
          label="Destination Name"
          description="A name to identify this destination in your dashboard"
          placeholder="e.g., Production RTMP Endpoint"
          value={labelField.field.value}
          onChange={labelField.field.onChange}
          onBlur={labelField.field.onBlur}
          error={labelField.fieldState.error?.message}
          maxLength={SIMULCAST_CONSTRAINTS.LABEL_MAX}
          withAsterisk
        />

        <TextInput
          label="Server URL"
          placeholder="rtmp://live.example.com/app"
          value={serverUrlField.field.value}
          onChange={serverUrlField.field.onChange}
          onBlur={serverUrlField.field.onBlur}
          error={serverUrlField.fieldState.error?.message}
          maxLength={SIMULCAST_CONSTRAINTS.RTMP_SERVER_URL_MAX}
          description={`${serverUrlField.field.value.length}/${SIMULCAST_CONSTRAINTS.RTMP_SERVER_URL_MAX}`}
          styles={{ input: { fontFamily: 'monospace' } }}
          withAsterisk
        />

        <PasswordInput
          label="Stream Key"
          placeholder="Enter stream key"
          value={streamKeyField.field.value}
          onChange={streamKeyField.field.onChange}
          onBlur={streamKeyField.field.onBlur}
          error={streamKeyField.fieldState.error?.message}
          maxLength={SIMULCAST_CONSTRAINTS.RTMP_STREAM_KEY_MAX}
          description={`${streamKeyField.field.value.length}/${SIMULCAST_CONSTRAINTS.RTMP_STREAM_KEY_MAX}`}
          withAsterisk
        />

        <SimpleGrid cols={2}>
          <TextInput
            label="Username"
            placeholder="Optional"
            value={userField.field.value}
            onChange={userField.field.onChange}
            onBlur={userField.field.onBlur}
            error={userField.fieldState.error?.message}
            maxLength={SIMULCAST_CONSTRAINTS.RTMP_USER_MAX}
          />

          <PasswordInput
            label="Password"
            placeholder="Optional"
            value={passField.field.value}
            onChange={passField.field.onChange}
            onBlur={passField.field.onBlur}
            error={passField.fieldState.error?.message}
            maxLength={SIMULCAST_CONSTRAINTS.RTMP_PASS_MAX}
          />
        </SimpleGrid>

        <Switch
          label="Auto-retry on failure"
          description="Automatically attempt to reconnect if the destination disconnects"
          checked={retryField.field.value}
          onChange={(e) => retryField.field.onChange(e.currentTarget.checked)}
        />

        <Group justify="flex-end" mt="sm">
          <Button variant="outline" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" loading={isPending} disabled={!isDirty}>
            {submitLabel}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
